import { normalizeApplicableTo } from '../Tabs/applicableTo';

/**
 * Return type of a configured function, used by the Formula tab to pick its operators and RHS
 * widget. Lives in its own module (no antd / config.js imports) so it is unit-testable — importing
 * QueryBuilder.js pulls the whole widget config graph in.
 */

/**
 * Type of a function given an ALREADY-RESOLVED field map. Callers must hand in the map that
 * actually contains `func.args.field`; see resolveFunctionReturnType.
 */
export const checkReturnTypeOfFunc = (func, fields, aggregateFunction = {}) => {
  if (func.functionName === 'match') {
    return 'boolean';
  }

  const fieldType = fields?.[func.args.field]?.type;

  if (fieldType === 'datetime') {
    return 'datetime';
  }

  return (
    aggregateFunction[fieldType]?.find(
      ({ value }) =>
        value?.toLowerCase() === func.args.aggregationFunction?.toLowerCase()
    )?.funcReturnType || 'boolean'
  );
};

/**
 * The field map(s) a function aggregates over, per its OWN `args.applicableTo` — mirroring
 * `getApplicableFields` in Tabs/Functions.js. Falls back to the variant's inputType for older
 * variants saved before applicableTo existed.
 */
export const fieldsForFunction = (func, allFields, triggeredOn = 'transaction') =>
  normalizeApplicableTo(func?.args?.applicableTo, [triggeredOn]).reduce(
    (acc, scope) => (allFields?.[scope] ? { ...acc, ...allFields[scope] } : acc),
    {}
  );

/**
 * FIN-13351: type a function by resolving its aggregated field in the scope the function itself
 * declares. Looking the field up in the variant's inputType map only meant an activity-scoped
 * function inside a Transaction variant resolved to nothing, fell through to the 'boolean' default,
 * and lost its numeric operators — so a stored `greater_or_equal` was dropped by Utils.checkTree
 * and the variant reported "Incomplete rules found" with an empty right-hand side.
 */
export const resolveFunctionReturnType = (
  func,
  allFields,
  aggregateFunction = {},
  triggeredOn = 'transaction'
) =>
  checkReturnTypeOfFunc(
    func,
    fieldsForFunction(func, allFields, triggeredOn),
    aggregateFunction
  );
